import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import {
  ContactoByFiltersQueryParamsProps,
  bulkDeleteByContactoId,
  deleteContactoById,
  getContactoByFilters,
} from "api/Contacto.service"
import { ContactoReadingDtoProps } from "api/DTOs/ContactoReadingDto"
import axios from "axios"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { CONTACTOS } from "routes/RoutePaths"
import { FilterMenu } from "./FilterMenu"
import { ModalEliminarContacto } from "./components/ModalEliminarContacto"
import { CONTACTOS_TABLE_FILTER } from "./constants/ContactosFilters"
import { CONTACTOS_COLUMNS } from "./constants/contactosColumns"
import useContactos from "./hooks/useContactos"

export const Contactos = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { toggleIsContactCreatedDispatch, isContactCreated } =
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    useContactos()

  const [propsDeleteModal, setPropsDeleteModal] = useState<{
    show: boolean
    contactos: Array<ContactoReadingDtoProps>
  }>({
    show: false,
    contactos: [],
  })

  const dtRef = useRef<DataTablePrimeReact<ContactoReadingDtoProps[]>>(null)
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const [selection, setSelection] = useState<ContactoReadingDtoProps[]>([])

  const [sortingCriteria, setSortingCriteria] = useState<
    ContactoByFiltersQueryParamsProps["SortingCriteria"]
  >({
    sort: undefined,
    property: undefined,
  })

  const defaultFilters = () => {
    const storedData = JSON.parse(
      sessionStorage.getItem("contactosTableFilters") ?? "null"
    )
    if (storedData === null) return CONTACTOS_TABLE_FILTER()
    return storedData
  }

  const [contactosFilters, setContactosFilters] = useState(defaultFilters)
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const contactosCancelToken = useRef(axios.CancelToken.source())
  const fetchContactos = useCallback(
    () => (queryParams: ContactoByFiltersQueryParamsProps) => {
      contactosCancelToken.current?.cancel()
      contactosCancelToken.current = axios.CancelToken.source()
      return getContactoByFilters(
        {
          ...queryParams,
          MaxResultCount: queryParams.MaxResultCount ?? pagination.itemsPerPage,
          SkipCount: queryParams.SkipCount ?? pagination.skipCount,
        },
        contactosCancelToken.current.token
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchContactos,
    })

  const handleRowDelete = (rowData: ContactoReadingDtoProps) => {
    setPropsDeleteModal({ show: true, contactos: [rowData] })
  }
  const handleDelete = async () => {
    const idsArray = propsDeleteModal.contactos?.flatMap(
      ({ id }) => id as string
    )
    const isSingleDelete = idsArray.length === 1
    const singleId = isSingleDelete
      ? propsDeleteModal.contactos?.[0]?.id ?? ""
      : ""
    if (isSingleDelete) await deleteContactoById(singleId)
    if (!isSingleDelete) await bulkDeleteByContactoId(idsArray)
    await fetchTableData()
    setPropsDeleteModal({ show: false, contactos: [] })
  }

  useEffect(() => {
    if (isContactCreated) {
      fetchTableData()
      toggleIsContactCreatedDispatch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContactCreated])

  useEffect(() => {
    const updateContactosFilters = {
      ...contactosFilters,
      pagination,
    }
    sessionStorage.setItem(
      "contactosTableFilters",
      JSON.stringify(updateContactosFilters)
    )
  }, [pagination, contactosFilters])

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
  }

  const onRowClick = async ({ data }: { data: ContactoReadingDtoProps }) => {
    if (!data.id) return
    const path = `${CONTACTOS}/${data.id}`
    history.push(path)
  }

  return (
    <>
      <FilterMenu
        maxResultCount={pagination.itemsPerPage}
        resetPagination={handleResetPagination}
        serviceQuery={setServiceQuery}
        skipCount={pagination.skipCount}
        sortingCriteria={sortingCriteria}
        openVulkDeleteModal={() =>
          setPropsDeleteModal({ show: true, contactos: selection })
        }
        selectedContactos={selection}
        contactosFilters={contactosFilters}
        setContactosFilters={setContactosFilters}
      />
      <div className='tw-flex tw-flex-col tw-h-[calc(100%-82px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={CONTACTOS_COLUMNS(t, handleRowDelete, onRowClick)}
            className='tw-h-full'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            dataKey='id'
            lazy
            scrollable
            selection={selection}
            selectionMode='checkbox'
            size='small'
            selectionColumnMode='multiple'
            onRowClick={onRowClick}
            onSelectionChange={({
              value,
            }: {
              originalEvent: React.SyntheticEvent
              value: ContactoReadingDtoProps[]
              type?: "multiple" | "all" | "checkbox" | "row" | undefined
            }) => {
              setSelection(value)
            }}
            loading={isLoading}
            emptyMessage={t("primeReact.table.emptyMessage")}
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>
      <ModalEliminarContacto
        isOpen={propsDeleteModal.show}
        selectedContacts={propsDeleteModal.contactos}
        onCancel={() => {
          setPropsDeleteModal({ show: false, contactos: [] })
        }}
        onConfirm={handleDelete}
      />
    </>
  )
}
